import { useEffect, useSyncExternalStore } from 'react';
import { normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { logger } from '@/core/logging';
import { getStoredOrganizationCode } from '@/shared/services/organization/organizationCodeStorage';
import { getOrganization } from '../api/organizationStep1.service';
import type { OrganizationRecord } from '../models';

/**
 * Whether the organization a Mega Admin provisions exists yet.
 *
 * `GET /organizations/{code}` decides where a Mega Admin works:
 *
 *   empty (404, no body, or no code at all)  ->  Organization Setup, to create it
 *   an organization                          ->  the Dashboard, showing it as a card
 *
 * Three places ask -- the landing, the sidebar and the dashboard -- so the answer is held once, per
 * organization code, in a module-level store rather than fetched by each. `refreshProvisionedOrganization`
 * re-asks after the wizard creates or completes the organization, and every subscriber moves together.
 *
 * A failed read that is not a 404 is treated as "not provisioned": the wizard is where a Mega Admin could
 * already always go, so an unreadable organization falls back to the behaviour that existed before.
 */

export type ProvisionedOrganization =
  | { status: 'loading' }
  | { status: 'absent' }
  | { status: 'present'; organization: OrganizationRecord };

const LOADING: ProvisionedOrganization = { status: 'loading' };
const ABSENT: ProvisionedOrganization = { status: 'absent' };

let current: ProvisionedOrganization = LOADING;
/** The code `current` answers for; a different code (a new sign-in) starts over. */
let answeredFor: string | null | undefined;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: ProvisionedOrganization): void {
  current = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): ProvisionedOrganization {
  return current;
}

async function load(code: string | null): Promise<void> {
  if (!code) {
    publish(ABSENT);
    return;
  }
  try {
    const organization = await getOrganization(code, { background: true });
    /* An envelope with no organization in it is the "empty response" too. */
    publish(organization?.organizationCode ? { status: 'present', organization } : ABSENT);
  } catch (err: unknown) {
    const error = normalizeError(err);
    if (error.status !== 404) {
      logger.warn('Could not read the provisioned organization; treating it as not set up', {
        code,
        status: error.status,
      });
    }
    publish(ABSENT);
  }
}

/** Asks again, for the code in storage now. Resolves once every subscriber has the new answer. */
export function refreshProvisionedOrganization(): Promise<void> {
  const code = getStoredOrganizationCode();
  answeredFor = code;
  inFlight = load(code).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Says the organization may have changed -- the wizard created, completed or edited it.
 *
 * Re-asks straight away when something is showing the answer (for a Mega Admin, the sidebar always is), so
 * the menu and the dashboard move with it; otherwise just forgets, and the next reader asks. Never a
 * request nobody reads.
 */
export function invalidateProvisionedOrganization(): void {
  if (listeners.size > 0) {
    void refreshProvisionedOrganization();
    return;
  }
  answeredFor = undefined;
  current = LOADING;
}

/** Forgets the answer, so the next sign-in asks for its own organization. */
export function resetProvisionedOrganization(): void {
  answeredFor = undefined;
  inFlight = null;
  publish(LOADING);
}

/**
 * The provisioned organization, fetched on first use.
 *
 * @param enabled only a Mega Admin asks; every other role gets `absent` without a request.
 */
export function useProvisionedOrganization(enabled: boolean): ProvisionedOrganization {
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    if (!enabled) {
      /* Signed out, or a different role: the next Mega Admin sign-in asks afresh. */
      if (answeredFor !== undefined) resetProvisionedOrganization();
      return;
    }
    const code = getStoredOrganizationCode();
    if (inFlight || (answeredFor === code && current.status !== 'loading')) return;
    if (answeredFor !== code && current.status !== 'loading') publish(LOADING);
    void refreshProvisionedOrganization();
  }, [enabled]);

  return enabled ? state : ABSENT;
}
